import { ElementType, ReactNode } from 'react';
import { cx } from './cx';

/**
 * The single source of truth for type in Regask UI. Every component references
 * these role tokens (or renders <Text>) instead of inventing its own
 * `text-sm font-semibold` — so size, weight, leading and tracking live in
 * exactly one place. If a component needs text, it uses one of these roles.
 */
export const typography = {
  display: 'text-[32px] leading-[1.1] font-bold tracking-[-0.025em]', // page hero
  title: 'text-xl leading-tight font-bold tracking-[-0.01em]', // card / section
  subtitle: 'text-base leading-relaxed font-normal', // lead under a title
  body: 'text-[15px] leading-relaxed', // running text, hints, options
  bodyMedium: 'text-[15px] font-medium', // emphasized body
  label: 'text-sm font-medium', // form-control labels
  button: 'text-sm font-semibold', // button / tab labels
  buttonLg: 'text-base font-semibold', // large button label
  caption: 'text-[13px] leading-snug', // small meta
  overline: 'text-[11px] font-bold uppercase tracking-[0.06em] leading-tight',
  mono: 'font-mono [font-feature-settings:"tnum"_1,"zero"_1]', // tabular data
} as const;

export type TextVariant =
  | 'display'
  | 'title'
  | 'subtitle'
  | 'body'
  | 'caption'
  | 'overline';

export type TextTone =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'muted'
  | 'accent';

const toneClasses: Record<TextTone, string> = {
  primary: 'text-fg',
  secondary: 'text-fg-secondary',
  tertiary: 'text-fg-tertiary',
  muted: 'text-fg-muted',
  accent: 'text-brand-text',
};

const defaultTag: Record<TextVariant, ElementType> = {
  display: 'h1',
  title: 'h2',
  subtitle: 'p',
  body: 'p',
  caption: 'span',
  overline: 'span',
};

export interface TextProps {
  variant?: TextVariant;
  tone?: TextTone;
  /** Render in Geist Mono with tabular figures — counts, metrics, refs. */
  mono?: boolean;
  as?: ElementType;
  className?: string;
  children: ReactNode;
}

/**
 * The one text primitive. Pick a semantic `variant` and `tone`; add `mono`
 * when the content is machine data. All sizing comes from `typography`.
 */
export const Text = ({
  variant = 'body',
  tone = 'primary',
  mono = false,
  as,
  className,
  children,
}: TextProps) => {
  const Tag = as ?? defaultTag[variant];
  return (
    <Tag
      className={cx(
        typography[variant],
        toneClasses[tone],
        mono && typography.mono,
        className,
      )}
    >
      {children}
    </Tag>
  );
};
